import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  ChevronRight,
  Eraser,
  RotateCcw,
  RotateCw,
  GripVertical,
  Pencil,
  Type,
  Undo,
  Redo,
  Palette,
  Settings,
  X,
} from "lucide-react";

const Whiteboard = ({ onClose, style }) => {
  const canvasRef = useRef(null);
  const masterCanvasRef = useRef(null);
  const contentRef = useRef(null);
  const textInputRef = useRef(null);

  const [context, setContext] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#0c0c0c");
  const [lineWidth, setLineWidth] = useState(4);
  const [fontSize, setFontSize] = useState(26);

  const [width, setWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);

  const [isAddingText, setIsAddingText] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [currentText, setCurrentText] = useState("");

  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const colorPalette = [
    "#0c0c0c", "#ef4444", "#3b82f6", "#10b981", "#f59e0b",
    "#8b5cf6", "#ec4899", "#6b7280", "#f97316", "#06b6d4"
  ];

  const updateHistoryButtons = useCallback(() => {
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  }, []);

  const saveToHistory = useCallback(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas || !context) return;

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      
      historyRef.current.push(imageData);
      historyIndexRef.current = historyRef.current.length - 1;
      
      if (historyRef.current.length > 50) {
        historyRef.current.shift();
        historyIndexRef.current = historyRef.current.length - 1;
      }
      
      updateHistoryButtons();
    } catch (err) {
      setError("Failed to save to history");
    }
  }, [context, updateHistoryButtons]);
  const handleResize = useCallback(
    (e) => {
      if (!isResizing || !contentRef.current) return;

      const containerRight = contentRef.current.getBoundingClientRect().right;
      const newWidth = containerRight - e.clientX;

      if (newWidth >= 400 && newWidth <= 1200) {
        setWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResize);
      window.addEventListener("mouseup", stopResize);
    }
    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [isResizing, handleResize]);

  const commitText = useCallback(() => {
    if (!currentText.trim() || !context) {
      return; 
    }

    try {
      const textToRender = currentText;
      const positionToRender = { ...textPosition };
      
      context.font = `${fontSize}px Mali, sans-serif`;
      context.fillStyle = color;
      context.textBaseline = 'top';
      
      context.fillText(textToRender, positionToRender.x, positionToRender.y);

      const masterCtx = masterCanvasRef.current?.getContext("2d");
      if (masterCtx) {
        masterCtx.font = `${fontSize}px Mali, sans-serif`;
        masterCtx.fillStyle = color;
        masterCtx.textBaseline = 'top';
        masterCtx.fillText(textToRender, positionToRender.x, positionToRender.y);
      }

      saveToHistory();
      
      setIsAddingText(false);
      setCurrentText("");
      setTextPosition({ x: 0, y: 0 });
      
    } catch (err) {
      setError("Failed to add text");
    }
  }, [currentText, textPosition, fontSize, color, context, saveToHistory]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isAddingText && e.target === textInputRef.current) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (currentText.trim()) {
            commitText();
          }
          return;
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancelText();
          return;
        }
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
        }
      }

      if (!isAddingText) {
        switch (e.key.toLowerCase()) {
          case 'p':
            setTool('pen');
            break;
          case 'e':
            setTool('eraser');
            break;
          case 't':
            setTool('text');
            break;
        }
      }

      if (e.key === 'Escape' && isAddingText) {
        cancelText();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAddingText, currentText, commitText]);

  const initializeCanvas = useCallback(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      if (!masterCanvasRef.current) {
        masterCanvasRef.current = document.createElement("canvas");
        masterCanvasRef.current.width = 1200; 
        masterCanvasRef.current.height = canvas.height;
      }

      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      setContext(ctx);
      
      setTimeout(() => {
        if (ctx) {
          const initialImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          historyRef.current = [initialImageData];
          historyIndexRef.current = 0;
          updateHistoryButtons();
        }
      }, 0);
    } catch (err) {
      setError("Failed to initialize canvas");
    }
  }, []);

  useEffect(() => {
    initializeCanvas();
  }, [initializeCanvas]);



  const handleUndo = useCallback(() => {
    try {
      if (historyIndexRef.current > 0) {
        historyIndexRef.current -= 1;
        const imageData = historyRef.current[historyIndexRef.current];
        
        if (imageData && context) {
          context.putImageData(imageData, 0, 0);
          updateHistoryButtons();
        }
      }
    } catch (err) {
      setError("Undo failed");
    }
  }, [context, updateHistoryButtons]);

  const handleRedo = useCallback(() => {
    try {
      if (historyIndexRef.current < historyRef.current.length - 1) {
        historyIndexRef.current += 1;
        const imageData = historyRef.current[historyIndexRef.current];
        
        if (imageData && context) {
          context.putImageData(imageData, 0, 0);
          updateHistoryButtons();
        }
      }
    } catch (err) {
      setError("Redo failed");
    }
  }, [context, updateHistoryButtons]);

  const updateCanvasSize = useCallback(() => {
    try {
      const canvas = canvasRef.current;
      const masterCanvas = masterCanvasRef.current;
      if (!canvas || !context || !masterCanvas) return;

      const masterCtx = masterCanvas.getContext("2d");

      masterCtx.drawImage(canvas, 0, 0);

      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      context.drawImage(masterCanvas, 0, 0);

      context.lineJoin = "round";
      context.lineCap = "round";
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
    } catch (err) {
      setError("Failed to resize canvas");
    }
  }, [context]);

  useEffect(() => {
    if (context) {
      updateCanvasSize();
    }
  }, [width, updateCanvasSize]);

  const startResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const stopResize = () => {
    setIsResizing(false);
  };

  const getCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  };

  const handleCanvasClick = (e) => {
    if (tool !== 'text') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (isAddingText) {
      if (currentText.trim()) {
        commitText();
      } else {
        cancelText();
      }
      return;
    }
    const { offsetX, offsetY } = getCoordinates(e);
    setTextPosition({ x: offsetX, y: offsetY });
    setIsAddingText(true);
    setCurrentText("");
    
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
      }
    }, 10);
  };

  const startDrawing = (e) => {
    if (tool === 'text') {
      return; 
    }

    const { offsetX, offsetY } = getCoordinates(e);

    context.beginPath();
    context.moveTo(offsetX, offsetY);

    const masterCtx = masterCanvasRef.current?.getContext("2d");
    if (masterCtx) {
      masterCtx.lineJoin = "round";
      masterCtx.lineCap = "round";
      masterCtx.beginPath();
      masterCtx.moveTo(offsetX, offsetY);
    }

    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || tool === 'text') return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    const masterCtx = masterCanvasRef.current?.getContext("2d");

    const currentColor = tool === "eraser" ? "#ffffff" : color;
    const currentLineWidth = tool === "eraser" ? lineWidth * 3 : lineWidth;

    context.strokeStyle = currentColor;
    context.lineWidth = currentLineWidth;
    context.lineTo(offsetX, offsetY);
    context.stroke();

    if (masterCtx) {
      masterCtx.strokeStyle = currentColor;
      masterCtx.lineWidth = currentLineWidth;
      masterCtx.lineTo(offsetX, offsetY);
      masterCtx.stroke();
    }
  };

  const stopDrawing = () => {
    if (isDrawing && tool !== 'text') {
      context.closePath();
      const masterCtx = masterCanvasRef.current?.getContext("2d");
      if (masterCtx) {
        masterCtx.closePath();
      }
      saveToHistory();
    }
    setIsDrawing(false);
  };

  const cancelText = () => {
    setIsAddingText(false);
    setCurrentText("");
    setTextPosition({ x: 0, y: 0 });
  };

  const clearCanvas = () => {
    try {
      const canvas = canvasRef.current;
      const masterCanvas = masterCanvasRef.current;

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);

      if (masterCanvas) {
        const masterCtx = masterCanvas.getContext("2d");
        masterCtx.fillStyle = '#ffffff';
        masterCtx.fillRect(0, 0, masterCanvas.width, masterCanvas.height);
      }

      saveToHistory();
    } catch (err) {
      setError("Failed to clear canvas");
    }
  };

  const switchTool = (newTool) => {
    if (isAddingText) {
      if (currentText.trim()) {
        commitText();
      } else {
        cancelText();
      }
    }
    
    setTool(newTool);
    setShowColorPicker(false);
    setShowSettings(false);
  };

  const getCursorStyle = () => {
    if (isAddingText) return 'text';
    
    switch (tool) {
      case 'pen':
        return 'crosshair';
      case 'eraser':
        return 'grab';
      case 'text':
        return 'text';
      default:
        return 'default';
    }
  };

  const closeError = () => setError(null);

  return (
    <div
      ref={contentRef}
      style={{ width: `${width}px`, ...style }}
      className="h-[81vh] bg-white bg-opacity-90 shadow-lg flex flex-col font-mali z-10 relative"
    >
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-red-100 border border-red-300 text-red-700 px-4 py-2 z-50 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={closeError} className="ml-2 hover:bg-red-200 rounded p-1">
            <X size={16} />
          </button>
        </div>
      )}

      <div
        className="absolute left-0 top-0 h-full flex items-center cursor-ew-resize z-20 group"
        onMouseDown={startResize}
        title="Drag to resize canvas"
      >
        <div className="w-6 h-12 bg-gray-100 group-hover:bg-gray-200 rounded-r flex items-center justify-center transition-colors">
          <GripVertical
            size={20}
            className="text-gray-500 group-hover:text-gray-700"
          />
        </div>
      </div>

      <div className="p-3 border-b bg-white">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            title="Close whiteboard"
          >
            <ChevronRight size={30} />
          </button>
          <h2 className="text-2xl font-bold">Start writing here</h2>
          <div className="w-8" />
        </div>
      </div>

      <div className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full bg-white"
          style={{ cursor: getCursorStyle() }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onClick={handleCanvasClick}
        />

        {isAddingText && (
          <div
            className="absolute bg-white border-2 border-blue-400 rounded shadow-lg"
            style={{
              left: Math.min(textPosition.x, width - 200),
              top: Math.min(textPosition.y, 400),
              zIndex: 1000,
              minWidth: '150px',
            }}
          >
            <input
              ref={textInputRef}
              type="text"
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              className="w-full px-2 py-1 border-none outline-none bg-transparent"
              style={{
                fontSize: `${fontSize}px`,
                color: color,
                fontFamily: 'Mali, sans-serif',
                minWidth: '150px',
              }}
              placeholder="Type here..."
              autoFocus
            />
            <div className="px-2 py-1 text-xs text-gray-500 border-t bg-gray-50">
              Press Enter to add text • Escape to cancel
            </div>
          </div>
        )}

        {/* Tool Controls */}
        <div className="absolute top-4 right-4 z-10">
          <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg p-2">
            
            {/* History Controls */}
            <div className="flex gap-2">
              <button
                onClick={handleUndo}
                disabled={!canUndo}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={`Undo (Ctrl+Z) - ${historyIndexRef.current}/${historyRef.current.length - 1}`}
              >
                <Undo size={20} />
              </button>
              <button
                onClick={handleRedo}
                disabled={!canRedo}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={`Redo (Ctrl+Y) - ${historyIndexRef.current}/${historyRef.current.length - 1}`}
              >
                <Redo size={20} />
              </button>
            </div>

            <div className="border-t pt-2">
              {/* Tool Selection */}
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => switchTool('pen')}
                  className={`p-2 rounded-lg transition-colors ${
                    tool === 'pen' ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                  title="Pen Tool (P)"
                >
                  <Pencil size={20} />
                </button>
                <button
                  onClick={() => switchTool('eraser')}
                  className={`p-2 rounded-lg transition-colors ${
                    tool === 'eraser' ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                  title="Eraser Tool (E)"
                >
                  <Eraser size={20} />
                </button>
                <button
                  onClick={() => switchTool('text')}
                  className={`p-2 rounded-lg transition-colors ${
                    tool === 'text' ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                  title="Text Tool (T)"
                >
                  <Type size={20} />
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
                  title="Color Picker"
                >
                  <Palette size={20} />
                  <div
                    className="absolute bottom-1 right-1 w-3 h-3 rounded-full border border-white"
                    style={{ backgroundColor: color }}
                  />
                </button>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Settings"
                >
                  <Settings size={20} />
                </button>
                <button
                  onClick={clearCanvas}
                  className="p-2 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors"
                  title="Clear Canvas"
                >
                  <RotateCcw size={20} />
                </button>
              </div>
            </div>
          </div>

          {showColorPicker && (
            <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg p-3 z-20">
              <div className="grid grid-cols-5 gap-2 mb-3">
                {colorPalette.map((paletteColor) => (
                  <button
                    key={paletteColor}
                    onClick={() => {
                      setColor(paletteColor);
                      setShowColorPicker(false);
                    }}
                    className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                      color === paletteColor ? 'border-blue-500' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: paletteColor }}
                    title={`Select color ${paletteColor}`}
                  />
                ))}
              </div>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-full h-8 rounded cursor-pointer"
                title="Custom color picker"
              />
            </div>
          )}

          {showSettings && (
            <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg p-4 z-20 min-w-48">
              <h3 className="font-semibold mb-3">Drawing Settings</h3>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">
                  Font Size: {fontSize}px
                </label>
                <input
                  type="range"
                  min="8"
                  max="72"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="w-full mb-3"
                />
                <label className="block text-sm font-medium mb-1">
                  Line Width: {lineWidth}px
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={lineWidth}
                  onChange={(e) => setLineWidth(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <button
                onClick={() => setShowSettings(false)}
                className="w-full mt-2 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>

        <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 rounded px-3 py-1 text-sm text-gray-600">
          {width}px × {canvasRef.current?.height || 0}px
        </div>
      </div>

      <div className="absolute bottom-4 right-4 text-xs text-gray-500 bg-white bg-opacity-90 rounded px-2 py-1">
        <div>P: Pen | E: Eraser | T: Text</div>
        <div>Ctrl+Z: Undo | Ctrl+Y: Redo</div>
        <div>Click canvas to add text • Enter to commit</div>
      </div>
    </div>
  );
};

export default Whiteboard;
